import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Card,
    CardBody,
    Spinner,
    Chip,
    Tooltip,
} from "@heroui/react";
import { LuCircleCheck, LuTriangleAlert, LuArrowLeft } from "react-icons/lu";
import { ThemedText } from "@/components/ui";
import { useFetchData } from "@/hooks";
import { formatThousands } from "@/utils";

const FormulaListModal = ({ isOpen, onClose, onBack, onSelectFormula }) => {
    const { data: formulas, isLoading } = useFetchData(
        isOpen ? "material-formulas/available-for-production" : null,
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            scrollBehavior="inside"
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex items-center gap-2">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={onBack}
                            >
                                <LuArrowLeft size={18} />
                            </Button>
                            <ThemedText size="md">Pilih Formula</ThemedText>
                        </ModalHeader>
                        <ModalBody>
                            {isLoading ? (
                                <div className="flex justify-center p-10">
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <ThemedText size="sm" variant="secondary">
                                        Pilih salah satu formula yang tersedia:
                                    </ThemedText>
                                    <div className="flex flex-col gap-3">
                                        {(formulas || []).length === 0 ? (
                                            <ThemedText
                                                variant="secondary"
                                                className="p-5 text-center"
                                            >
                                                Tidak ada formula.
                                            </ThemedText>
                                        ) : (
                                            formulas.map((formula) => (
                                                <FormulaCard
                                                    key={formula.id}
                                                    formula={formula}
                                                    onSelect={() =>
                                                        onSelectFormula(formula)
                                                    }
                                                />
                                            ))
                                        )}
                                    </div>
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="light"
                                size="sm"
                                onPress={onClose}
                            >
                                Batal
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

const FormulaCard = ({ formula, onSelect }) => {
    const isAvailable = formula.is_available;

    // Build ingredients string with + separator (qty unit name)
    const ingredientsStr =
        formula.items
            ?.map(
                (item) =>
                    `${formatThousands(item.quantity)} ${item.unit?.name || ""} ${item.material?.name || "?"}`,
            )
            .join(" + ") || "-";

    // Build missing items tooltip content (butuh X, tersedia Y)
    const missingItemsList = formula.missing_items || [];

    return (
        <Card
            isPressable={isAvailable}
            isDisabled={!isAvailable}
            onPress={isAvailable ? onSelect : undefined}
            className={`border transition-all ${
                isAvailable
                    ? "border-default-200 hover:border-primary-400"
                    : "border-default-100 opacity-70"
            }`}
            shadow="none"
        >
            <CardBody className="p-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <ThemedText size="sm">{formula.name}</ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            {ingredientsStr}
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            Hasil:{" "}
                            <span className="font-semibold">
                                {formula.material?.name}
                            </span>{" "}
                            -{" "}
                            <span className="font-semibold">
                                {formatThousands(formula.quantity)}{" "}
                                {formula.unit?.name}
                            </span>
                        </ThemedText>
                    </div>
                    {isAvailable ? (
                        <Chip
                            size="sm"
                            color="success"
                            variant="flat"
                            startContent={<LuCircleCheck />}
                        >
                            Tersedia
                        </Chip>
                    ) : (
                        <Tooltip
                            content={
                                <div className="flex flex-col gap-1 p-1">
                                    <ThemedText
                                        size="xs"
                                        className="font-semibold"
                                    >
                                        Bahan kurang:
                                    </ThemedText>
                                    {missingItemsList.length > 0 ? (
                                        missingItemsList.map((item, i) => (
                                            <ThemedText key={i} size="xs">
                                                • {item.material_name}: butuh{" "}
                                                {item.required}
                                            </ThemedText>
                                        ))
                                    ) : (
                                        <ThemedText size="xs">
                                            Stok tidak mencukupi
                                        </ThemedText>
                                    )}
                                </div>
                            }
                        >
                            <Chip
                                size="sm"
                                color="danger"
                                variant="flat"
                                startContent={<LuTriangleAlert />}
                            >
                                Stok Kurang
                            </Chip>
                        </Tooltip>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default FormulaListModal;
